import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import { getApiErrorMessage } from '@moko/utils';
import { FormActions, defaultTimeZone, useFormLayoutStyles } from '@moko/ui';
import {
  createContractorAvailabilityRule,
  type ICreateContractorAvailabilityRuleRequest,
} from '../../services/contractorAvailabilityRuleService';
import { toApiDateOnly } from '../../utils/dateOnly';
import {
  ContractorAvailabilityForm,
  type ContractorAvailabilityFormValues,
} from './ContractorAvailabilityForm';

const emptyForm = (): ContractorAvailabilityFormValues => ({
  daysOfWeek: [1],
  dayOfWeek: 1,
  startTime: '',
  endTime: '',
  timezoneId: defaultTimeZone(),
  effectiveFrom: '',
  effectiveTo: '',
  status: true,
});

export type ContractorAvailabilityCreateProps = {
  contractorId?: number;
  submitLabel: string;
  onDraftCreate?: (
    data: Omit<ICreateContractorAvailabilityRuleRequest, 'contractorId'>
  ) => void;
  onClose: () => void;
  onSaved: () => void;
};

const ContractorAvailabilityCreate = ({
  contractorId,
  submitLabel,
  onDraftCreate,
  onClose,
  onSaved,
}: ContractorAvailabilityCreateProps) => {
  const { t } = useTranslation();
  const styles = useFormLayoutStyles();
  const [saveError, setSaveError] = useState<string | null>(null);
  const form = useForm<ContractorAvailabilityFormValues>({
    defaultValues: emptyForm(),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, formState } = form;
  const submitting = formState.isSubmitting;

  const onSubmit = handleSubmit(async (values) => {
    if (!values.effectiveFrom || !values.effectiveTo) return;
    setSaveError(null);
    const data = {
      daysOfWeek: values.daysOfWeek,
      startTime: values.startTime,
      endTime: values.endTime,
      timezoneId: values.timezoneId,
      effectiveFrom: toApiDateOnly(values.effectiveFrom),
      effectiveTo: toApiDateOnly(values.effectiveTo),
      status: values.status,
    };
    try {
      if (contractorId == null) {
        onDraftCreate?.(data);
        return;
      }
      await createContractorAvailabilityRule(new AbortController(), {
        ...data,
        contractorId,
      });
      onSaved();
    } catch (err: unknown) {
      setSaveError(
        getApiErrorMessage(
          err,
          t('failedToSaveRule', 'Failed to save availability rule.')
        )
      );
    }
  });

  return (
    <FormProvider {...form}>
      <form className={styles.form} onSubmit={onSubmit}>
        {saveError && (
          <MessageBar intent="error">
            <MessageBarBody>{saveError}</MessageBarBody>
          </MessageBar>
        )}
        <ContractorAvailabilityForm disabled={submitting} multipleDays />
        <FormActions
          disabled={submitting}
          submitting={submitting}
          submitLabel={submitLabel}
          onClose={onClose}
        />
      </form>
    </FormProvider>
  );
};

export default ContractorAvailabilityCreate;
